import {
	type ComponentPropsWithoutRef,
	type ReactNode,
	useId,
	useState,
} from 'react'
import { cn } from '#app/utils/misc'
import { siteContent } from '#app/utils/site/content'

export type AuthFieldType = 'text' | 'email' | 'tel' | 'password'

/** What conform's `getInputProps(field, { type })` hands us, plus HTML attrs. */
export type AuthInputProps = ComponentPropsWithoutRef<'input'> & {
	key?: React.Key | null
}

/**
 * Classes shared by every pill input on the auth screens (the phone field
 * and the OTP boxes borrow them). 16px text on phones so iOS never zooms.
 */
export const AUTH_INPUT_CLASS =
	'tt-auth-input block h-[50px] w-full rounded-full border border-tt-line bg-white pl-[18px] pr-[18px] text-[16px] text-tt-ink outline-none transition-[border-color,background-color] duration-200 focus:border-tt-ink aria-invalid:border-tt-danger read-only:cursor-not-allowed read-only:bg-tt-inset read-only:text-tt-muted disabled:cursor-not-allowed disabled:bg-tt-inset disabled:text-tt-muted min-[601px]:h-[clamp(46px,6.2vh,56px)] min-[601px]:px-[22px] min-[601px]:text-[15px]'

/** The field's bottom margin, so rows and single fields rhyme. */
export const AUTH_FIELD_GAP =
	'tt-auth-field mb-2.5 min-[601px]:mb-[clamp(8px,1.5vh,14px)]'

const errorIdFor = (id: string) => `${id}-error`

/**
 * A pill input with a floating label (CSS only: see .tt-auth-label in
 * auth.css), an optional eye toggle for passwords, and the error state the
 * screens need. Pass the object from conform's `getInputProps`; `label`,
 * `type` and `autoComplete` are explicit so the markup stays predictable.
 */
export function AuthField({
	label,
	inputProps,
	errors,
	type = 'text',
	autoComplete,
	hint,
	className,
}: {
	label: string
	inputProps: AuthInputProps
	errors?: Array<string | null | undefined> | null
	type?: AuthFieldType
	autoComplete?: string
	/** Shown faintly inside the pill while focused and empty. */
	hint?: string
	className?: string
}) {
	const fallbackId = useId()
	const {
		key,
		id: givenId,
		className: inputClassName,
		placeholder: _placeholder,
		...rest
	} = inputProps
	const id = givenId ?? fallbackId
	const messages = (errors ?? []).filter(
		(error): error is string => typeof error === 'string' && error !== '',
	)
	const hasError = messages.length > 0
	const errorId = errorIdFor(id)
	const [shown, setShown] = useState(false)
	const isPassword = type === 'password'
	const fields = siteContent.auth.fields

	return (
		<div className={cn(AUTH_FIELD_GAP, className)}>
			<div className="relative">
				<input
					key={key ?? undefined}
					{...rest}
					id={id}
					type={isPassword && shown ? 'text' : type}
					autoComplete={autoComplete}
					placeholder={hint ?? ' '}
					aria-invalid={hasError ? true : rest['aria-invalid']}
					aria-describedby={hasError ? errorId : rest['aria-describedby']}
					className={cn(
						AUTH_INPUT_CLASS,
						isPassword && 'pr-[52px] min-[601px]:pr-[52px]',
						inputClassName,
					)}
				/>
				<label htmlFor={id} className="tt-auth-label">
					{label}
				</label>
				{isPassword ? (
					<button
						type="button"
						onClick={() => setShown((value) => !value)}
						aria-label={shown ? fields.hidePassword : fields.showPassword}
						aria-pressed={shown}
						className="absolute right-4 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-tt-ink hover:bg-tt-inset focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tt-ink"
					>
						<EyeIcon off={!shown} />
					</button>
				) : null}
			</div>
			{hasError ? (
				<p
					id={errorId}
					className="mt-1.5 pl-[18px] text-[14px] font-medium text-tt-danger min-[601px]:pl-[22px]"
				>
					{messages.join(' ')}
				</p>
			) : null}
		</div>
	)
}

/** Two fields side by side (first and last name), on phones as well. */
export function AuthFieldRow({
	children,
	className,
}: {
	children: ReactNode
	className?: string
}) {
	return (
		<div
			className={cn('grid grid-cols-2 gap-2.5 min-[601px]:gap-3.5', className)}
		>
			{children}
		</div>
	)
}

/** The reference's eye: crossed while the password is hidden. */
function EyeIcon({ off }: { off: boolean }) {
	return (
		<svg
			viewBox="0 0 24 24"
			width="22"
			height="22"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.6}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			{off ? (
				<>
					<path d="M3 3l18 18" />
					<path d="M10.6 5.1A9.8 9.8 0 0 1 12 5c5 0 8.5 4.3 9.5 7-.4 1-1.2 2.4-2.4 3.7M6.2 6.6C4.3 7.9 3 9.8 2.5 12c1 2.7 4.5 7 9.5 7 1.7 0 3.2-.5 4.5-1.2" />
					<path d="M9.9 10a3 3 0 0 0 4.2 4.2" />
				</>
			) : (
				<>
					<path d="M2.5 12c1-2.7 4.5-7 9.5-7s8.5 4.3 9.5 7c-1 2.7-4.5 7-9.5 7s-8.5-4.3-9.5-7z" />
					<circle cx="12" cy="12" r="3" />
				</>
			)}
		</svg>
	)
}
